import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import TextField from '@mui/material/TextField';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import { useTranslation } from 'react-i18next';

const AddEditDialog = ({ open, row, onClose, onSave, isEdit, data, selectedProject }) => {
    const { t } = useTranslation();
    const projects = [...new Set(data.filter(p => p.project !== undefined).map(p => p.project))];

    const [editData, setEditData] = useState(row || {
        id: '',
        project: selectedProject,
        subDomain: '',
        ip: '0.0.0.0',
        description: '',
        timeout: '3600',
        isDisabled: false
    });

    const [errors, setErrors] = useState({});
    useEffect(() => {
        setEditData(row || {
            id: '',
            project: '',
            subDomain: '',
            ip: '0.0.0.0',
            description: '',
            timeout: '3600',
            isDisabled: false
        });
    }, [row]);

    const validate = () => {
        const newErrors = {};
        const dnsRegex = /^(?!-)[A-Za-z0-9-]{1,63}(?<!-)(\.[A-Za-z0-9-]{1,63})*$/;
        const ipRegex = /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;

        const isDuplicate = data.some(p => p.subDomain && p.subDomain.toLowerCase() === (editData.subDomain + "." + editData.project).toLowerCase() && p.id !== editData.id);

        if (isDuplicate && !isEdit) {
            newErrors.subDomain = t('errors.subdomainExists');
        }

        if (!dnsRegex.test(editData.subDomain)) {
            newErrors.subDomain = t('errors.invalidSubdomain');
        }

        if (!dnsRegex.test(editData.project)) {
            newErrors.project = t('errors.invalidProjectName');
        }

        if (!ipRegex.test(editData.ip) && isEdit) {
            newErrors.ip = t('errors.invalidIpAddress');
        }

        const timeoutValue = parseInt(editData.timeout);
        if (isNaN(timeoutValue) || timeoutValue <= 0) {
            newErrors.timeout = t('errors.invalidTimeout');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        let { name, value } = e.target;
        if (name === 'project' || name === 'subDomain') {
            value = value.toLowerCase();
        }
        else if (name === 'timeout' && isNaN(value)) {
            value = '3600';
        }
        setEditData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSave = () => {
        if (!editData.project && selectedProject) {
            editData.project = projects.find(p => p === selectedProject);
        }
        if (validate()) {
            editData.subDomain = isEdit ? `${editData.subDomain}` : `${editData.subDomain}.${editData.project}`;
            onSave(editData);
            onClose();
        }
    };

    const handleCancel = () => {
        setEditData(row || {
            id: '',
            project: '',
            subDomain: '',
            ip: '0.0.0.0',
            description: '',
            timeout: '3600',
            isDisabled: false
        });
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle>
                {isEdit ? t('editDdns.title') : t('addDdns.title')}
            </DialogTitle>
            <DialogContent>
                <TextField
                    sx={{ display: 'none' }}
                    hidden={true}
                    margin="dense"
                    name="id"
                    label={t('id')}
                    fullWidth
                    value={editData?.id || ''}
                    disabled={true}
                />
                <TextField
                    margin="dense"
                    label={t('project')}
                    fullWidth
                    required={true}
                    disabled={isEdit}
                    error={!!errors.project}
                    helperText={errors.project}
                    value={editData?.project || selectedProject || ''}
                />
                <TextField
                    margin="dense"
                    name="subDomain"
                    label={t('subdomain')}
                    fullWidth
                    value={isEdit ? editData?.subDomain?.length > 1 ? editData?.subDomain?.split('.')[0] : editData?.subDomain : editData?.subDomain || ''}
                    required={true}
                    disabled={isEdit}
                    error={!!errors.subDomain}
                    helperText={errors.subDomain}
                    onChange={handleChange}
                    slotProps={{
                        input: {
                            endAdornment: <InputAdornment position="end">.{editData?.project}</InputAdornment>,
                        },
                    }}
                />
                <TextField
                    margin="dense"
                    name="ip"
                    label={t('ip')}
                    fullWidth
                    value={editData?.ip || ''}
                    disabled={!isEdit}
                    error={!!errors.ip}
                    helperText={errors.ip}
                    onChange={handleChange}
                />
                <TextField
                    margin="dense"
                    name="description"
                    label={t('description')}
                    fullWidth
                    value={editData?.description || ''}
                    onChange={handleChange}
                />
                <TextField
                    margin="dense"
                    name="timeout"
                    label={t('updateInterval')}
                    fullWidth
                    value={editData?.timeout || '3600'}
                    required={true}
                    onChange={handleChange}
                    error={!!errors.timeout}
                    helperText={errors.timeout}
                    type="number"
                    slotProps={{
                        input: {
                            endAdornment: <InputAdornment position="end">{t('secs')}</InputAdornment>,
                        },
                    }}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCancel} color="primary">
                    {t('cancel')}
                </Button>
                <Button onClick={() => handleSave(editData)} color="primary">
                    {t('save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddEditDialog;
